import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import { toast } from "sonner";
import { FloppyDisk, Eye, ArrowLeft, Trash } from "@phosphor-icons/react";

const CATEGORIES = [
  { id: "circuit_quebec", label: "Circuit Québec" },
  { id: "evenements", label: "Événements" },
  { id: "athletes", label: "Athlètes" },
  { id: "entrainement", label: "Entraînement" },
  { id: "nutrition", label: "Nutrition" },
  { id: "mental", label: "Mental" },
];

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}

const EMPTY = {
  title: "", slug: "", category: "circuit_quebec",
  featured_image_url: "", excerpt: "", content: "",
  status: "brouillon", tags: [], meta_title: "", meta_description: "",
  evenement_id: "", athlete_id: "",
};

export default function BlogEditor() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [tagInput, setTagInput] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [preview, setPreview] = useState(false);
  const [events, setEvents] = useState([]);
  const [athletes, setAthletes] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/events").then(r => r.data).catch(() => []),
      api.get("/athletes").then(r => r.data).catch(() => []),
    ]).then(([ev, ath]) => { setEvents(ev || []); setAthletes(ath || []); });

    if (isEdit) {
      api.get(`/admin/blog/posts/${id}`)
        .then(r => {
          setForm({
            ...EMPTY, ...r.data,
            evenement_id: r.data.evenement_id || "",
            athlete_id: r.data.athlete_id || "",
          });
          setSlugTouched(true);
        })
        .catch(() => { toast.error("Article introuvable"); navigate("/app/blog"); })
        .finally(() => setLoading(false));
    }
  }, [id]);

  const computedSlug = useMemo(() => {
    if (slugTouched && form.slug) return form.slug;
    return slugify(form.title);
  }, [form.title, form.slug, slugTouched]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/\s+/g, "-");
    if (t && !form.tags.includes(t)) setField("tags", [...form.tags, t]);
    setTagInput("");
  };
  const removeTag = (t) => setField("tags", form.tags.filter(x => x !== t));

  const onUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image trop volumineuse (max 3 Mo)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/blog/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField("featured_image_url", r.data.url);
      toast.success(`Image téléversée (${Math.round(r.data.size / 1024)} Ko)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du téléversement");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async (publishNow = null) => {
    if (!form.title.trim()) { toast.error("Le titre est requis"); return; }
    setSaving(true);
    const payload = {
      ...form,
      slug: computedSlug,
      status: publishNow !== null ? (publishNow ? "publie" : "brouillon") : form.status,
      evenement_id: form.evenement_id || null,
      athlete_id: form.athlete_id || null,
    };
    Object.keys(payload).forEach(k => { if (payload[k] === "") delete payload[k]; });
    try {
      let res;
      if (isEdit) {
        res = await api.put(`/admin/blog/posts/${id}`, payload);
      } else {
        res = await api.post("/admin/blog/posts", payload);
      }
      toast.success(payload.status === "publie" ? "Article publié" : "Brouillon enregistré");
      if (!isEdit) navigate(`/app/blog/${res.data.id}/editer`, { replace: true });
      else setForm(f => ({ ...f, ...res.data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm("Supprimer cet article ?")) return;
    try {
      await api.delete(`/admin/blog/posts/${id}`);
      toast.success("Article supprimé");
      navigate("/app/blog");
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  if (loading) return <div className="p-10 text-muted-foreground ticker">Chargement…</div>;

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <Link to="/app/blog" className="inline-flex items-center gap-2 ticker text-muted-foreground hover:text-[#D90429] mb-6" data-testid="blog-editor-back">
        <ArrowLeft size={14} /> Retour aux articles
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <div className="ticker text-[#D90429] mb-2">{isEdit ? "ÉDITION" : "NOUVEL ARTICLE"}</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">{form.title || "Sans titre"}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPreview(p => !p)} data-testid="blog-editor-preview-toggle" className="hard-border px-4 py-2 ticker hover:bg-secondary flex items-center gap-2">
            <Eye size={14} /> {preview ? "Éditer" : "Aperçu"}
          </button>
          <button onClick={() => save(false)} disabled={saving} data-testid="blog-editor-save-draft" className="hard-border px-4 py-2 ticker hover:bg-secondary flex items-center gap-2">
            <FloppyDisk size={14} /> Brouillon
          </button>
          <button onClick={() => save(true)} disabled={saving} data-testid="blog-editor-publish" className="hard-border px-4 py-2 ticker bg-[#D90429] text-white border-[#D90429] hover:bg-black hover:border-black">
            {form.status === "publie" ? "Mettre à jour" : "Publier"}
          </button>
          {isEdit && (
            <button onClick={remove} data-testid="blog-editor-delete" className="hard-border p-2 hover:bg-[#D90429] hover:text-white" title="Supprimer">
              <Trash size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-8">
        {/* Main editor */}
        <div className="space-y-5">
          <div>
            <label className="ticker text-xs text-muted-foreground mb-1 block">Titre</label>
            <input
              data-testid="blog-editor-title"
              value={form.title}
              onChange={e => setField("title", e.target.value)}
              placeholder="Titre de l'article"
              className="w-full hard-border px-4 py-3 text-xl font-bold bg-transparent focus:outline-none focus:border-[#D90429]"
            />
          </div>

          <div>
            <label className="ticker text-xs text-muted-foreground mb-1 block">Slug (URL)</label>
            <div className="flex items-center hard-border focus-within:border-[#D90429]">
              <span className="px-3 text-muted-foreground ticker">/blog/</span>
              <input
                data-testid="blog-editor-slug"
                value={computedSlug}
                onChange={e => { setSlugTouched(true); setField("slug", slugify(e.target.value)); }}
                className="flex-1 px-2 py-2 bg-transparent focus:outline-none ticker"
              />
            </div>
          </div>

          <div>
            <label className="ticker text-xs text-muted-foreground mb-1 block">Image mise en avant</label>
            <div className="flex gap-2 items-stretch">
              <input
                data-testid="blog-editor-image"
                value={form.featured_image_url || ""}
                onChange={e => setField("featured_image_url", e.target.value)}
                placeholder="URL https://… ou téléverser ↘"
                className="flex-1 hard-border px-4 py-2 bg-transparent focus:outline-none focus:border-[#D90429]"
              />
              <label data-testid="blog-editor-image-upload-label" className="hard-border px-4 py-2 ticker cursor-pointer hover:bg-secondary flex items-center gap-2 whitespace-nowrap">
                {uploading ? "…" : "Téléverser"}
                <input
                  data-testid="blog-editor-image-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={onUploadImage}
                  disabled={uploading}
                />
              </label>
              {form.featured_image_url && (
                <button
                  type="button"
                  data-testid="blog-editor-image-clear"
                  onClick={() => setField("featured_image_url", "")}
                  className="hard-border px-3 py-2 hover:bg-[#D90429] hover:text-white"
                  title="Retirer l'image"
                >
                  ×
                </button>
              )}
            </div>
            {form.featured_image_url && (
              <img src={form.featured_image_url} alt="" className="mt-2 max-h-48 hard-border" />
            )}
            <div className="text-[10px] ticker text-muted-foreground mt-1">JPEG, PNG, WEBP, GIF · max 3 Mo</div>
          </div>

          <div>
            <label className="ticker text-xs text-muted-foreground mb-1 block">Extrait (200 car. max)</label>
            <textarea
              data-testid="blog-editor-excerpt"
              value={form.excerpt || ""}
              onChange={e => setField("excerpt", e.target.value.slice(0, 200))}
              rows={2}
              placeholder="Extrait court, généré automatiquement si vide"
              className="w-full hard-border px-4 py-2 bg-transparent focus:outline-none focus:border-[#D90429]"
            />
            <div className="text-[10px] ticker text-muted-foreground text-right mt-1">{(form.excerpt || "").length}/200</div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="ticker text-xs text-muted-foreground">Contenu (Markdown)</label>
              <a href="https://www.markdownguide.org/basic-syntax/" target="_blank" rel="noreferrer" className="ticker text-[10px] text-muted-foreground hover:text-[#D90429]">AIDE MARKDOWN ↗</a>
            </div>
            {preview ? (
              <div className="hard-border p-6 prose prose-neutral dark:prose-invert max-w-none blog-content min-h-[400px]" data-testid="blog-editor-preview">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content || "_Aperçu vide_"}</ReactMarkdown>
              </div>
            ) : (
              <textarea
                data-testid="blog-editor-content"
                value={form.content || ""}
                onChange={e => setField("content", e.target.value)}
                rows={20}
                placeholder="# Titre&#10;&#10;Écris ton article en **Markdown**…"
                className="w-full hard-border px-4 py-3 bg-transparent focus:outline-none focus:border-[#D90429] font-mono text-sm leading-relaxed"
              />
            )}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="hard-border p-4">
            <div className="ticker text-xs text-muted-foreground mb-2">STATUT</div>
            <div className="text-lg font-bold" data-testid="blog-editor-status">
              {form.status === "publie" ? <span className="text-[#D90429]">PUBLIÉ</span> : "BROUILLON"}
            </div>
            {form.published_at && (
              <div className="text-xs text-muted-foreground mt-1">depuis le {new Date(form.published_at).toLocaleString("fr-CA")}</div>
            )}
          </div>

          <div>
            <label className="ticker text-xs text-muted-foreground mb-2 block">Catégorie</label>
            <select
              data-testid="blog-editor-category"
              value={form.category}
              onChange={e => setField("category", e.target.value)}
              className="w-full hard-border px-3 py-2 bg-transparent focus:outline-none focus:border-[#D90429]"
            >
              {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>

          <div>
            <label className="ticker text-xs text-muted-foreground mb-2 block">Tags</label>
            <div className="flex flex-wrap gap-1 mb-2">
              {form.tags.map(t => (
                <span key={t} className="ticker text-[10px] hard-border px-2 py-1 flex items-center gap-1">
                  #{t}
                  <button onClick={() => removeTag(t)} className="hover:text-[#D90429]">×</button>
                </span>
              ))}
            </div>
            <input
              data-testid="blog-editor-tag-input"
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
              placeholder="Ajouter un tag + Entrée"
              className="w-full hard-border px-3 py-2 bg-transparent focus:outline-none focus:border-[#D90429] text-sm"
            />
          </div>

          <div className="hard-border p-4 space-y-3">
            <div className="ticker text-xs text-muted-foreground">SEO</div>
            <input
              data-testid="blog-editor-meta-title"
              value={form.meta_title || ""}
              onChange={e => setField("meta_title", e.target.value)}
              placeholder="Titre SEO (sinon = titre)"
              className="w-full hard-border px-3 py-2 bg-transparent focus:outline-none focus:border-[#D90429] text-sm"
            />
            <textarea
              data-testid="blog-editor-meta-desc"
              value={form.meta_description || ""}
              onChange={e => setField("meta_description", e.target.value.slice(0, 160))}
              rows={3}
              placeholder="Description SEO (160 car.)"
              className="w-full hard-border px-3 py-2 bg-transparent focus:outline-none focus:border-[#D90429] text-sm"
            />
            <div className="text-[10px] ticker text-muted-foreground text-right">{(form.meta_description || "").length}/160</div>
          </div>

          <div className="hard-border p-4 space-y-3">
            <div className="ticker text-xs text-muted-foreground">LIENS RELATIFS</div>
            <div>
              <label className="ticker text-[10px] text-muted-foreground mb-1 block">Événement</label>
              <select
                data-testid="blog-editor-event"
                value={form.evenement_id || ""}
                onChange={e => setField("evenement_id", e.target.value)}
                className="w-full hard-border px-3 py-2 bg-transparent focus:outline-none focus:border-[#D90429] text-sm"
              >
                <option value="">— Aucun —</option>
                {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
              </select>
            </div>
            <div>
              <label className="ticker text-[10px] text-muted-foreground mb-1 block">Athlète</label>
              <select
                data-testid="blog-editor-athlete"
                value={form.athlete_id || ""}
                onChange={e => setField("athlete_id", e.target.value)}
                className="w-full hard-border px-3 py-2 bg-transparent focus:outline-none focus:border-[#D90429] text-sm"
              >
                <option value="">— Aucun —</option>
                {athletes.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
              </select>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
